import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fieldWorkApi } from '../api/field-work-api';
import { fulfillmentApi } from '../api/fulfillment-api';
import { ConflictError, FulfillmentError, NotFoundError } from '../api/errors';
import { shipmentsApi } from '../api/shipments-api';
import { stockApi } from '../api/stock-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { PartialFailureNotice } from '../components/PartialFailureNotice';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  amendmentActionLabels,
  canAmendLines,
  canPick,
  canRemovePicks,
  isOpenOrder,
  lineComplete,
  linePicked,
  orderProgress,
  orderTitle,
  overrideReasonLabels,
  requirementLabel,
  type FulfillmentOrder,
  type FulfillmentOrderLine,
  type FulfillmentPick,
  type StockBalance,
} from '../domain/fulfillment';
import { shipmentStateLabels } from '../domain/shipment';
import { AmendLineDialog } from '../features/fulfillment/AmendLineDialog';
import { EditLinesDrawer } from '../features/fulfillment/EditLinesDrawer';
import { fulfillmentErrorMessage, isUnknownOutcome } from '../features/fulfillment/error-message';
import {
  CancelOrderDialog,
  HandoverDialog,
  RemovePickDialog,
} from '../features/fulfillment/OrderDialogs';
import { OrderStateBadge, ProgressMeter } from '../features/fulfillment/OrderStateBadge';
import { describePartChoice } from '../features/fulfillment/part-choice';
import { locationShortfall } from '../features/fulfillment/stock-locations';
import { ScanPanel } from '../features/fulfillment/ScanPanel';
import { NewShipmentDrawer } from '../features/shipment-create/NewShipmentDrawer';
import { ShipmentLink } from '../features/shipments/ShipmentLink';
import { STOCK_QUERY_KEYS } from '../features/stock/use-stock-references';
import {
  useFulfillmentReferences,
  type FulfillmentReferences,
} from '../features/fulfillment/use-fulfillment-references';

type Dialog =
  | { kind: 'cancel' }
  | { kind: 'handover' }
  | { kind: 'lines' }
  | { kind: 'remove'; pick: FulfillmentPick }
  | { kind: 'amend'; line: FulfillmentOrderLine };

function AmendmentLog({
  order,
  references,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
}) {
  if (order.amendments.length === 0) return null;
  const label = (requirement: FulfillmentOrder['lines'][number]['requirement'] | undefined) =>
    requirement
      ? requirementLabel(requirement, { part: references.partName, stock: references.stockName })
      : '—';
  const amendments = [...order.amendments].sort((a, b) =>
    (b.amendedAt ?? '').localeCompare(a.amendedAt ?? ''),
  );
  return (
    <section className="pick-log amendment-log" aria-labelledby="amendment-log-heading">
      <h2 id="amendment-log-heading">Line changes</h2>
      <ul className="pick-log-list">
        {amendments.map((amendment, index) => (
          <li
            key={`${amendment.lineId ?? ''}-${amendment.amendedAt ?? index}`}
            className="pick-log-entry"
          >
            <div className="pick-log-main">
              <span className="state-badge">{amendmentActionLabels[amendment.action]}</span>
              <span>
                {label(amendment.before)}
                {amendment.action === 'CANCEL_LINE' ? '' : ` → ${label(amendment.after)}`}
              </span>
              {amendment.reason ? <span className="pick-log-line">{amendment.reason}</span> : null}
            </div>
            <div className="pick-log-meta">
              <span>
                {amendment.amendedBy ?? '—'}
                {amendment.amendedAt ? ` · ${new Date(amendment.amendedAt).toLocaleString()}` : ''}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PickList({
  order,
  references,
  balances,
  targetLineId,
  onScanFor,
  onAmend,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
  /** Loaded only while a line waits for stock, to say where else it is. */
  balances?: readonly StockBalance[] | undefined;
  targetLineId?: string | undefined;
  onScanFor: (lineId: string) => void;
  onAmend: (line: FulfillmentOrderLine) => void;
}) {
  return (
    <section className="pick-list" aria-labelledby="pick-list-heading">
      <h2 id="pick-list-heading">Pick list</h2>
      <ul className="pick-lines">
        {order.lines.map((line) => {
          const done = lineComplete(line);
          const name = requirementLabel(line.requirement, {
            part: references.partName,
            stock: references.stockName,
          });
          const unit =
            line.requirement.type === 'STOCK' ? references.unit(line.requirement.stockItemId) : '';
          const waiting = done ? 0 : (line.waitingQuantity ?? 0);
          // Release takes only from the line's location: say when the stock
          // it waits for is on another shelf already.
          const shortfall =
            waiting > 0 && line.requirement.type === 'STOCK'
              ? locationShortfall({
                  locations: references.locations,
                  balances,
                  stockItemId: line.requirement.stockItemId,
                  locationId: line.locationId,
                  quantity: waiting,
                  moveQuantity: line.requirement.quantity - linePicked(line),
                  locationName: references.locationName,
                })
              : undefined;
          return (
            <li key={line.id} className={`pick-line${done ? ' is-complete' : ''}`}>
              <div className="pick-line-heading">
                <strong>{name}</strong>
                <span
                  className={
                    done
                      ? 'state-badge state-badge-ready'
                      : waiting > 0
                        ? 'state-badge state-badge-attention'
                        : 'state-badge'
                  }
                >
                  {done ? 'Done' : waiting > 0 ? 'Waiting for stock' : 'To pick'}
                </span>
              </div>
              <div className="pick-line-detail">
                {line.requirement.type === 'SERIALIZED' ? (
                  <span>
                    {describePartChoice(
                      line.requirement.partIds,
                      references.parts,
                      references.partName,
                    )}
                  </span>
                ) : (
                  <span>
                    {references.stockName(line.requirement.stockItemId ?? '')}
                    {line.locationId || waiting === 0
                      ? ` · from ${references.locationName(line.locationId)}`
                      : ''}
                    {waiting > 0
                      ? ` · ${waiting}${unit ? ` ${unit}` : ''} waiting for stock: scan once received`
                      : ''}
                    {shortfall ? (
                      <span className="location-shortfall">
                        {` · ${shortfall.text}`}
                        {shortfall.alternative && canAmendLines(order) && linePicked(line) === 0
                          ? ': change the line to pick from there'
                          : ''}
                      </span>
                    ) : null}
                  </span>
                )}
                <ProgressMeter
                  complete={linePicked(line)}
                  total={line.requirement.quantity}
                  label={`${name}: ${linePicked(line)} of ${line.requirement.quantity}${unit ? ` ${unit}` : ''} picked`}
                />
              </div>
              {canPick(order) ? (
                <div className="pick-line-actions">
                  {!done ? (
                    <button
                      type="button"
                      className="link-button"
                      aria-pressed={targetLineId === line.id}
                      onClick={() => onScanFor(line.id)}
                      aria-label={`Scan for ${name}`}
                    >
                      {targetLineId === line.id ? 'Scanning for this line' : 'Scan for this line'}
                    </button>
                  ) : null}
                  {canAmendLines(order) ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onAmend(line)}
                      aria-label={`Change ${name}`}
                    >
                      Change line
                    </button>
                  ) : null}
                </div>
              ) : null}
              {line.itemIds.length > 0 ? (
                <ul className="picked-serials" aria-label={`Picked for ${name}`}>
                  {line.itemIds.map((id) => (
                    <li key={id}>
                      <Link className="mono" to={`/inventory/${encodeURIComponent(id)}`}>
                        {id}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PickLog({
  order,
  references,
  onRemove,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
  onRemove: (pick: FulfillmentPick) => void;
}) {
  if (order.picks.length === 0) return null;
  const lineName = (id: string | undefined) => {
    const line = order.lines.find((candidate) => candidate.id === id);
    return line
      ? requirementLabel(line.requirement, {
          part: references.partName,
          stock: references.stockName,
        })
      : '—';
  };
  const picks = [...order.picks].sort((a, b) => (b.pickedAt ?? '').localeCompare(a.pickedAt ?? ''));
  return (
    <section className="pick-log" aria-labelledby="pick-log-heading">
      <h2 id="pick-log-heading">Scans</h2>
      <ul className="pick-log-list">
        {picks.map((pick) => (
          <li
            key={pick.id}
            className={pick.removed ? 'pick-log-entry is-removed' : 'pick-log-entry'}
          >
            <div className="pick-log-main">
              <span className="mono">{pick.itemId ?? `${pick.quantity} × ${pick.code}`}</span>
              <span className="pick-log-line">{lineName(pick.lineId)}</span>
              {pick.manualOverride ? (
                <span className="attention-badge">
                  Typed:{' '}
                  {pick.overrideReason ? overrideReasonLabels[pick.overrideReason] : 'no reason'}
                  {pick.overrideNote ? ` — ${pick.overrideNote}` : ''}
                </span>
              ) : null}
              {pick.removed ? (
                <span className="state-badge state-badge-muted">
                  Removed{pick.removeReason ? `: ${pick.removeReason}` : ''}
                  {pick.removedBy ? ` by ${pick.removedBy}` : ''}
                </span>
              ) : null}
            </div>
            <div className="pick-log-meta">
              <span>
                {pick.pickedBy ?? '—'}
                {pick.pickedAt ? ` · ${new Date(pick.pickedAt).toLocaleString()}` : ''}
              </span>
              {!pick.removed && canRemovePicks(order) ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => onRemove(pick)}
                  aria-label={`Remove ${pick.itemId ?? pick.code}`}
                >
                  Remove
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkShipmentForm({
  order,
  onLinked,
  onReload,
}: {
  order: FulfillmentOrder;
  onLinked: (order: FulfillmentOrder) => void;
  onReload: () => void;
}) {
  const [shipmentId, setShipmentId] = useState('');
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const shipments = useQuery({
    queryKey: ['shipments'],
    queryFn: () => shipmentsApi.listShipments(),
  });
  // Linking copies the order's serials onto the shipment (tidaltech/koi#8105).
  const link = useMutation({
    mutationFn: (id: string) =>
      fulfillmentApi.updateOrder({ id: order.id, version: order.version, shipmentId: id }),
    onSuccess: onLinked,
    onError: (failure: unknown) =>
      setError({
        text: fulfillmentErrorMessage(failure, 'The shipment could not be linked.'),
        conflict: failure instanceof ConflictError,
      }),
  });
  return (
    <>
      <form
        className="inline-form"
        aria-label="Link a shipment"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          if (shipmentId) link.mutate(shipmentId);
        }}
      >
        <label>
          Link shipment
          <SearchableSelect
            ariaLabel="Shipment to link"
            className="shipment-link-select"
            value={shipmentId}
            onChange={setShipmentId}
            options={(shipments.data?.shipments ?? [])
              .slice()
              .sort((a, b) => b.shippedAt.localeCompare(a.shippedAt))
              .map((shipment) => ({
                value: shipment.id,
                label: shipment.destinationCustomer || shipment.id.slice(0, 8),
                detail: [
                  shipment.cargonizerConsignmentNumber,
                  shipment.destinationAddress.city,
                  new Date(shipment.shippedAt).toLocaleDateString(),
                  shipmentStateLabels[shipment.state],
                ]
                  .filter(Boolean)
                  .join(' · '),
              }))}
            placeholder="Choose a shipment"
          />
        </label>
        <button type="submit" disabled={!shipmentId || link.isPending}>
          {link.isPending ? 'Linking…' : 'Link'}
        </button>
        {/* Book the shipment from here and link it, instead of going via the
          field work request and back (Sivert, 2026-10-16). */}
        <button
          type="button"
          className="primary"
          onClick={() => {
            setError(null);
            setBooking(true);
          }}
          disabled={link.isPending}
        >
          Book shipment
        </button>
        {error ? (
          <p role="alert" className="form-error">
            {error.text}
            {error.conflict ? (
              <button type="button" className="link-button" onClick={onReload}>
                Reload order
              </button>
            ) : null}
          </p>
        ) : null}
      </form>
      {booking ? (
        <NewShipmentDrawer
          initialSiteId={order.siteId}
          initialReason={orderTitle(order)}
          onClose={() => setBooking(false)}
          onCreated={(id) => {
            setBooking(false);
            link.mutate(id);
          }}
        />
      ) : null}
    </>
  );
}

/** Picks, packs and hands over one order (tidaltech/koi#8084). */
export function FulfillmentOrderPage() {
  const { orderId = '' } = useParams();
  const queryClient = useQueryClient();
  const references = useFulfillmentReferences();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [chosenLineId, setChosenLineId] = useState<string>();
  const [releaseError, setReleaseError] = useState<{
    text: string;
    conflict: boolean;
    /** Not enough stock: the order may be released to wait for it. */
    shortfall: boolean;
  } | null>(null);
  const [releaseUnknown, setReleaseUnknown] = useState(false);

  const order = useQuery({
    queryKey: ['fulfillment-order', orderId],
    queryFn: () => fulfillmentApi.getOrder(orderId),
  });
  const requestId = order.data?.fieldWorkRequestId;
  const linkedRequest = useQuery({
    queryKey: ['field-work', requestId],
    queryFn: () => fieldWorkApi.getFieldWork(requestId ?? ''),
    enabled: Boolean(requestId),
  });
  const waitsForStock = Boolean(
    order.data?.lines.some((line) => !lineComplete(line) && (line.waitingQuantity ?? 0) > 0),
  );
  const balances = useQuery({
    queryKey: STOCK_QUERY_KEYS.balances,
    queryFn: () => stockApi.listStockBalances(),
    enabled: waitsForStock,
  });

  function show(updated: FulfillmentOrder): void {
    queryClient.setQueryData(['fulfillment-order', orderId], updated);
    void queryClient.invalidateQueries({ queryKey: ['fulfillment-orders'] });
  }

  function reload(): void {
    setDialog(null);
    setReleaseError(null);
    setReleaseUnknown(false);
    void order.refetch();
  }

  const release = useMutation({
    mutationFn: ({
      current,
      allowShortfall,
    }: {
      current: FulfillmentOrder;
      allowShortfall: boolean;
    }) => fulfillmentApi.releaseOrder(current.id, current.version, allowShortfall),
    onSuccess: (updated) => {
      setReleaseError(null);
      show(updated);
    },
    onError: (error: unknown) => {
      if (isUnknownOutcome(error)) setReleaseUnknown(true);
      else {
        setReleaseError({
          text: fulfillmentErrorMessage(error, 'The order could not be released.'),
          conflict: error instanceof ConflictError,
          shortfall: error instanceof FulfillmentError && error.reason === 'INSUFFICIENT_STOCK',
        });
      }
    },
  });

  if (order.isLoading) return <PageLoadingState label="Loading order…" />;
  if (order.isError || !order.data) {
    if (order.error instanceof NotFoundError) {
      return (
        <div className="empty-state">
          <strong>This order does not exist.</strong>
          <Link to="/fulfillment">Back to pick & pack</Link>
        </div>
      );
    }
    return (
      <ErrorState
        title="The order could not be loaded"
        rpcName="GetFulfillmentOrder"
        onRetry={() => void order.refetch()}
      />
    );
  }

  const current = order.data;
  const waitingLines = current.lines.filter(
    (line) => !lineComplete(line) && (line.waitingQuantity ?? 0) > 0,
  ).length;
  const progress = orderProgress(current);
  const title = orderTitle(current);
  // A chosen line stops being the scan target once it is fully picked or gone.
  const targetLine = current.lines.find((line) => line.id === chosenLineId);
  const targetLineId = targetLine && !lineComplete(targetLine) ? targetLine.id : undefined;
  const closeDialog = () => setDialog(null);
  const done = (updated: FulfillmentOrder) => {
    setDialog(null);
    show(updated);
  };

  return (
    <>
      <PageHeader
        backLink={<Link to="/fulfillment">← Pick & pack</Link>}
        title={title}
        titleAction={<OrderStateBadge state={current.state} />}
        description={`${references.siteName(current.siteId)} · ${references.customerName(current.customerId)}${
          current.dispatchDate
            ? ` · Dispatch ${new Date(current.dispatchDate).toLocaleDateString()}`
            : ''
        }`}
        actions={
          <>
            {current.state === 'DRAFT' ? (
              <>
                <button type="button" onClick={() => setDialog({ kind: 'lines' })}>
                  Edit lines
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => release.mutate({ current, allowShortfall: false })}
                  disabled={release.isPending}
                >
                  {release.isPending ? 'Releasing…' : 'Release for picking'}
                </button>
              </>
            ) : null}
            {current.state === 'READY_TO_HANDOVER' ? (
              <button
                type="button"
                className="primary"
                onClick={() => setDialog({ kind: 'handover' })}
              >
                Finish packing
              </button>
            ) : null}
            {current.state !== 'DRAFT' && current.state !== 'CANCELLED' ? (
              <Link to={`/fulfillment/${current.id}/packing-list`} className="button-link">
                Print packing list
              </Link>
            ) : null}
            {isOpenOrder(current) ? (
              <button
                type="button"
                className="button-danger"
                onClick={() => setDialog({ kind: 'cancel' })}
              >
                Cancel order
              </button>
            ) : null}
          </>
        }
      />

      {releaseError ? (
        <div role="alert" className="form-error">
          {releaseError.text}
          {releaseError.conflict ? (
            <button type="button" className="link-button" onClick={reload}>
              Reload order
            </button>
          ) : null}
          {releaseError.shortfall ? (
            <p>
              You can release it anyway: lines short of stock reserve what there is and wait for the
              rest. Everything else can be picked now, and packing can be finished once the rest is
              received and picked.{' '}
              <button
                type="button"
                onClick={() => release.mutate({ current, allowShortfall: true })}
                disabled={release.isPending}
              >
                Release and wait for stock
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      {releaseUnknown ? <PartialFailureNotice onCheckCurrentState={reload} /> : null}
      {current.fieldWorkRequestId ? (
        <p className="field-hint">
          For field work request{' '}
          <Link to={`/field-work/${current.fieldWorkRequestId}`}>
            {linkedRequest.data?.title || current.fieldWorkRequestId}
          </Link>
        </p>
      ) : null}

      <div className="order-summary" aria-label="Order summary">
        <ProgressMeter
          complete={progress.complete}
          total={progress.total}
          label={`${progress.complete} of ${progress.total} picked`}
        />
        {current.state === 'DRAFT' ? (
          <p>
            Draft: lines can still change. Releasing reserves the stock and opens the order for
            picking.
          </p>
        ) : null}
        {waitingLines > 0 ? (
          <p>
            Waiting for stock on {waitingLines === 1 ? 'one line' : `${waitingLines} lines`}.
            Packing can be finished once it has been received and picked.
          </p>
        ) : null}
        {current.state === 'CANCELLED' ? (
          <p>
            Cancelled{current.cancelledBy ? ` by ${current.cancelledBy}` : ''}
            {current.cancelReason ? `: ${current.cancelReason}` : ''}.
          </p>
        ) : null}
        {current.state === 'COMPLETED' ? (
          <p>
            {/* Packing is finished here; the carrier picks the goods up later,
                on the shipment. */}
            {current.handoverMethod === 'TECHNICIAN'
              ? `Handed to ${current.recipient ?? 'a technician'} (technician)`
              : current.recipient
                ? `Packed and handed to ${current.recipient}`
                : 'Packed and ready to ship'}
            {current.handedOverBy ? ` by ${current.handedOverBy}` : ''}
            {current.handedOverAt ? ` on ${new Date(current.handedOverAt).toLocaleString()}` : ''}.
          </p>
        ) : null}
        {current.shipmentId ? (
          <p>
            Shipment: <ShipmentLink shipmentId={current.shipmentId} />
          </p>
        ) : current.state === 'COMPLETED' && current.handoverMethod !== 'TECHNICIAN' ? (
          <LinkShipmentForm order={current} onLinked={show} onReload={reload} />
        ) : null}
        {current.notes ? <p className="order-notes">{current.notes}</p> : null}
      </div>

      <div className="pick-workspace">
        {canPick(current) ? (
          <ScanPanel
            order={current}
            references={references}
            onPicked={(result) => show(result.order)}
            targetLineId={targetLineId}
            onClearTarget={() => setChosenLineId(undefined)}
          />
        ) : null}
        <PickList
          order={current}
          references={references}
          balances={balances.data}
          targetLineId={targetLineId}
          onScanFor={(lineId) => setChosenLineId(lineId === targetLineId ? undefined : lineId)}
          onAmend={(line) => setDialog({ kind: 'amend', line })}
        />
      </div>
      <PickLog
        order={current}
        references={references}
        onRemove={(pick) => setDialog({ kind: 'remove', pick })}
      />
      <AmendmentLog order={current} references={references} />

      {dialog?.kind === 'cancel' ? (
        <CancelOrderDialog order={current} onClose={closeDialog} onDone={done} onReload={reload} />
      ) : null}
      {dialog?.kind === 'handover' ? (
        <HandoverDialog
          order={current}
          references={references}
          onClose={closeDialog}
          onDone={done}
          onReload={reload}
          onRemovePick={(pick) => setDialog({ kind: 'remove', pick })}
        />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <RemovePickDialog
          order={current}
          pick={dialog.pick}
          onClose={closeDialog}
          onDone={done}
          onReload={reload}
        />
      ) : null}
      {dialog?.kind === 'amend' ? (
        <AmendLineDialog
          order={current}
          line={dialog.line}
          references={references}
          onClose={closeDialog}
          onDone={done}
          onReload={reload}
        />
      ) : null}
      {dialog?.kind === 'lines' ? (
        <EditLinesDrawer
          order={current}
          references={references}
          onClose={closeDialog}
          onSaved={done}
          onReload={reload}
        />
      ) : null}
    </>
  );
}
